(() => {
  const SEARCH_SELECTOR = '[data-tvati-blog-search]';
  const BLOG_CARD_SELECTOR = '[data-tvati-blog-card-tone]';
  const LATEST_CARD_SELECTOR = '[data-tvati-latest-card-tone]';
  const CARD_SELECTOR = `${BLOG_CARD_SELECTOR}, ${LATEST_CARD_SELECTOR}`;
  /* ------------------------------------------------------------------------
     Automatic article image tone
     ------------------------------------------------------------------------
     Both the Blog/Journal cards and Latest Articles now use exactly the same
     image analysis. We sample the upper 40% of the article artwork because
     Tvati artwork normally carries its dominant background tone there.
     LIGHT image:
     - navy copy
     - white/light pill
     - blue pill dot
     - white arrow circle / blue arrow
     DARK image:
     - white copy
     - translucent white pill
     - yellow pill dot
     - white arrow circle / blue arrow
     ------------------------------------------------------------------------ */
  const applyBlogCardTone = (card, isLight) => {
    card.classList.toggle('tvati-blog-card--image-light', isLight);
    card.classList.toggle('tvati-blog-card--image-dark', !isLight);
  };
  const applyLatestCardTone = (card, isLight) => {
    card.classList.toggle('tvati-latest-card--image-light', isLight);
    card.classList.toggle('tvati-latest-card--image-dark', !isLight);
    if (isLight) {
      card.style.setProperty('--tvati-latest-card-text', 'var(--tvati-navy)');
      card.style.setProperty('--tvati-latest-pill-bg', 'rgb(255 255 255 / 54%)');
      card.style.setProperty('--tvati-latest-pill-text', 'var(--tvati-navy)');
      card.style.setProperty('--tvati-latest-pill-dot', 'var(--tvati-blue)');
      card.style.setProperty('--tvati-latest-arrow-bg', 'var(--tvati-white)');
      card.style.setProperty('--tvati-latest-arrow-icon', 'var(--tvati-blue)');
    } else {
      card.style.setProperty('--tvati-latest-card-text', 'var(--tvati-white)');
      card.style.setProperty('--tvati-latest-pill-bg', 'rgb(255 255 255 / 20%)');
      card.style.setProperty('--tvati-latest-pill-text', 'var(--tvati-white)');
      card.style.setProperty('--tvati-latest-pill-dot', 'var(--tvati-hibiscus)');
      card.style.setProperty('--tvati-latest-arrow-bg', 'var(--tvati-white)');
      card.style.setProperty('--tvati-latest-arrow-icon', 'var(--tvati-blue)');
    }
  };
  const analyseCardTone = (card) => {
    if (!card || card.dataset.tvatiBlogToneReady === 'true') return;
    const isLatest = card.matches(LATEST_CARD_SELECTOR);
    const image = isLatest
      ? card.querySelector('.tvati-latest-card__image')
      : card.querySelector('.tvati-blog-card__image');
    if (!image) {
      card.dataset.tvatiBlogToneReady = 'true';
      return;
    }
    const applyTone = () => {
      if (!image.naturalWidth || !image.naturalHeight) return;
      try {
        const canvas = document.createElement('canvas');
        const width = 30;
        const height = 30;
        canvas.width = width;
        canvas.height = height;
        const context = canvas.getContext('2d', {
          alpha: false,
          willReadFrequently: true
        });
        if (!context) return;
        context.drawImage(image, 0, 0, width, height);
        const pixels = context.getImageData(0, 0, width, height).data;
        let luminanceTotal = 0;
        let samples = 0;
        const sampleRows = Math.max(1, Math.floor(height * 0.4));
        for (let y = 0; y < sampleRows; y += 1) {
          for (let x = 0; x < width; x += 1) {
            const index = ((y * width) + x) * 4;
            const alpha = pixels[index + 3];
            if (alpha < 200) continue;
            const red = pixels[index];
            const green = pixels[index + 1];
            const blue = pixels[index + 2];
            const luminance =
              (0.2126 * red) +
              (0.7152 * green) +
              (0.0722 * blue);
            luminanceTotal += luminance;
            samples += 1;
          }
        }
        if (!samples) return;
        const averageLuminance = luminanceTotal / samples;
        const isLight = averageLuminance >= 155;
        if (isLatest) {
          applyLatestCardTone(card, isLight);
        } else {
          applyBlogCardTone(card, isLight);
        }
        card.dataset.tvatiBlogToneReady = 'true';
      } catch (error) {
        /*
         * If canvas sampling fails, do nothing. Latest Articles keeps the
         * configured fallback colour settings; Blog cards keep their existing
         * tone fallback.
         */
        card.dataset.tvatiBlogToneReady = 'true';
      }
    };
    if (image.complete) {
      applyTone();
      return;
    }
    image.addEventListener('load', applyTone, { once: true });
    image.addEventListener(
      'error',
      () => {
        card.dataset.tvatiBlogToneReady = 'true';
      },
      { once: true }
    );
  };
  const initCards = (root = document) => {
    if (root.matches?.(CARD_SELECTOR)) {
      analyseCardTone(root);
    }
    root.querySelectorAll?.(CARD_SELECTOR).forEach(analyseCardTone);
  };
  /* ------------------------------------------------------------------------
     Predictive article + same-page FAQ search
     ------------------------------------------------------------------------
     Help & Support can opt into FAQ search with:
       data-tvati-search-faqs="true"
     FAQ data is NOT duplicated. We index the rendered FAQ blocks already on
     the page, so editing the FAQ section automatically updates Help search.
     ------------------------------------------------------------------------ */
  const initSearch = (root = document) => {
    root.querySelectorAll?.(SEARCH_SELECTOR).forEach((search) => {
      if (search.dataset.tvatiBlogSearchReady === 'true') return;
      search.dataset.tvatiBlogSearchReady = 'true';
      const input = search.querySelector('[data-tvati-blog-search-input]');
      const results = search.querySelector('[data-tvati-blog-search-results]');
      const status = search.querySelector('[data-tvati-blog-search-status]');
      const clear = search.querySelector('[data-tvati-blog-search-clear]');
      if (!input || !results || !status) return;
      const rootUrl = search.dataset.rootUrl || '/';
      const blogUrl = search.dataset.blogUrl || '';
      const searchUrl = search.dataset.searchUrl || `${rootUrl}search`;
      const searchFaqs = search.dataset.tvatiSearchFaqs === 'true';
      const faqLimit = Math.max(
        1,
        Number.parseInt(search.dataset.tvatiFaqLimit || '6', 10) || 6
      );
      const faqDialogId = search.dataset.tvatiFaqDialog || '';
      const faqDialog = faqDialogId
        ? document.getElementById(faqDialogId)
        : null;
      const faqDialogTitle = faqDialog?.querySelector(
        '[data-tvati-help-faq-dialog-title]'
      );
      const faqDialogAnswer = faqDialog?.querySelector(
        '[data-tvati-help-faq-dialog-answer]'
      );
      const faqDialogCategory = faqDialog?.querySelector(
        '[data-tvati-help-faq-dialog-category]'
      );
      let timer = null;
      let controller = null;
      let lastFaqTrigger = null;
      const close = () => {
        search.classList.remove('is-open');
        input.setAttribute('aria-expanded', 'false');
      };
      const open = () => {
        search.classList.add('is-open');
        input.setAttribute('aria-expanded', 'true');
      };
      const clearResults = () => {
        results
          .querySelectorAll('.tvati-blog-search-result')
          .forEach((node) => node.remove());
      };
      const normalise = (value) =>
        String(value || '')
          .toLowerCase()
          .replace(/\s+/g, ' ')
          .trim();
      const faqIndex = () => {
        if (!searchFaqs) return [];
        const seen = new Set();
        const items = [];
        document
          .querySelectorAll('[data-tvati-faq-tabs] [data-tvati-faq-item]')
          .forEach((item) => {
            const questionButton = item.querySelector(
              '[data-tvati-faq-question]'
            );
            const questionNode = questionButton?.querySelector(
              ':scope > span:first-child'
            );
            const answerNode = item.querySelector(
              '.tvati-faq-tabs__answer'
            );
            if (!questionNode || !answerNode) return;
            const question = questionNode.textContent?.trim() || '';
            const answerText = answerNode.textContent?.trim() || '';
            const answerHtml = answerNode.innerHTML || '';
            if (!question || !answerText) return;
            const panel = item.closest('[data-tvati-faq-panel]');
            const category =
              panel?.dataset.tvatiFaqPanel?.trim() || 'general';
            const dedupeKey = `${normalise(question)}|${normalise(answerText)}`;
            if (seen.has(dedupeKey)) return;
            seen.add(dedupeKey);
            items.push({
              question,
              answerText,
              answerHtml,
              category
            });
          });
        return items;
      };
      const findFaqs = (query) => {
        if (!searchFaqs) return [];
        const normalisedQuery = normalise(query);
        if (!normalisedQuery) return [];
        const tokens = normalisedQuery
          .split(' ')
          .filter((token) => token.length > 1);
        return faqIndex()
          .map((faq) => {
            const question = normalise(faq.question);
            const answer = normalise(faq.answerText);
            let score = 0;
            if (question === normalisedQuery) score += 150;
            if (question.startsWith(normalisedQuery)) score += 100;
            if (question.includes(normalisedQuery)) score += 80;
            if (answer.includes(normalisedQuery)) score += 35;
            tokens.forEach((token) => {
              if (question.includes(token)) score += 18;
              if (answer.includes(token)) score += 5;
            });
            return { ...faq, score };
          })
          .filter((faq) => faq.score > 0)
          .sort((a, b) => b.score - a.score)
          .slice(0, faqLimit);
      };
      const resultImageUrl = (article) => {
        if (typeof article?.featured_image === 'string') {
          return article.featured_image;
        }
        if (article?.featured_image?.url) {
          return article.featured_image.url;
        }
        if (typeof article?.image === 'string') {
          return article.image;
        }
        if (article?.image?.url) {
          return article.image.url;
        }
        return '';
      };
      const makeArticleResult = (article) => {
        const link = document.createElement('a');
        link.className = 'tvati-blog-search-result';
        link.href = article.url;
        const media = document.createElement('span');
        media.className = 'tvati-blog-search-result__media';
        const imageUrl = resultImageUrl(article);
        if (imageUrl) {
          const img = document.createElement('img');
          img.src = imageUrl;
          img.alt = '';
          img.loading = 'lazy';
          media.appendChild(img);
        }
        const copy = document.createElement('span');
        const tag = document.createElement('span');
        tag.className = 'tvati-blog-search-result__tag';
        tag.textContent = 'HELP GUIDE';
        const title = document.createElement('span');
        title.className = 'tvati-blog-search-result__title';
        title.textContent = article.title || '';
        copy.append(tag, title);
        const arrow = document.createElement('span');
        arrow.className = 'tvati-blog-search-result__arrow';
        arrow.setAttribute('aria-hidden', 'true');
        link.append(media, copy, arrow);
        return link;
      };
      const categoryLabel = (category) => {
        const labels = {
          pet: 'PET FAQ',
          equine: 'EQUINE FAQ',
          human: 'HUMAN FAQ',
          general: 'FAQ'
        };
        return labels[category] || 'FAQ';
      };
      const openFaqDialog = (faq, trigger) => {
        if (!faqDialog || !faqDialogTitle || !faqDialogAnswer) return;
        lastFaqTrigger = trigger || null;
        faqDialogTitle.textContent = faq.question;
        faqDialogAnswer.innerHTML = faq.answerHtml;
        if (faqDialogCategory) {
          faqDialogCategory.textContent = categoryLabel(faq.category);
        }
        close();
        if (typeof faqDialog.showModal === 'function') {
          faqDialog.showModal();
        } else {
          faqDialog.setAttribute('open', '');
        }
      };
      const makeFaqResult = (faq) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className =
          'tvati-blog-search-result tvati-blog-search-result--faq';
        const media = document.createElement('span');
        media.className =
          'tvati-blog-search-result__media tvati-blog-search-result__media--faq';
        media.textContent = '?';
        media.setAttribute('aria-hidden', 'true');
        const copy = document.createElement('span');
        const tag = document.createElement('span');
        tag.className = 'tvati-blog-search-result__tag';
        tag.textContent = categoryLabel(faq.category);
        const title = document.createElement('span');
        title.className = 'tvati-blog-search-result__title';
        title.textContent = faq.question;
        copy.append(tag, title);
        const arrow = document.createElement('span');
        arrow.className = 'tvati-blog-search-result__arrow';
        arrow.setAttribute('aria-hidden', 'true');
        button.append(media, copy, arrow);
        button.addEventListener('click', () => {
          openFaqDialog(faq, button);
        });
        return button;
      };
      const setupFaqDialog = () => {
        if (!faqDialog || faqDialog.dataset.tvatiHelpFaqDialogReady === 'true') {
          return;
        }
        faqDialog.dataset.tvatiHelpFaqDialogReady = 'true';
        faqDialog
          .querySelectorAll('[data-tvati-help-faq-dialog-close]')
          .forEach((button) => {
            button.addEventListener('click', () => {
              if (typeof faqDialog.close === 'function') {
                faqDialog.close();
              } else {
                faqDialog.removeAttribute('open');
              }
            });
          });
        faqDialog.addEventListener('click', (event) => {
          if (event.target !== faqDialog) return;
          if (typeof faqDialog.close === 'function') {
            faqDialog.close();
          } else {
            faqDialog.removeAttribute('open');
          }
        });
        faqDialog.addEventListener('close', () => {
          if (lastFaqTrigger?.isConnected) {
            lastFaqTrigger.focus();
          } else {
            input.focus();
          }
        });
      };
      setupFaqDialog();
      const renderResults = ({ faqs = [], articles = [] }) => {
        clearResults();
        faqs.forEach((faq) => {
          results.appendChild(makeFaqResult(faq));
        });
        articles.forEach((article) => {
          results.appendChild(makeArticleResult(article));
        });
        const total = faqs.length + articles.length;
        if (!total) {
          status.textContent = searchFaqs
            ? 'NO MATCHING ANSWERS OR GUIDES'
            : 'NO MATCHING ARTICLES';
          return;
        }
        if (searchFaqs) {
          const parts = [];
          if (faqs.length) {
            parts.push(
              `${faqs.length} QUICK ANSWER${faqs.length === 1 ? '' : 'S'}`
            );
          }
          if (articles.length) {
            parts.push(
              `${articles.length} GUIDE${articles.length === 1 ? '' : 'S'}`
            );
          }
          status.textContent = parts.join(' · ');
          return;
        }
        status.textContent =
          `${articles.length} MATCH${articles.length === 1 ? '' : 'ES'}`;
      };
      const run = async (value) => {
        const query = value.trim();
        clear?.toggleAttribute('hidden', query.length === 0);
        if (query.length < 2) {
          controller?.abort();
          clearResults();
          status.textContent = 'TYPE AT LEAST 2 CHARACTERS';
          close();
          return;
        }
        controller?.abort();
        controller = new AbortController();
        const faqs = findFaqs(query);
        clearResults();
        status.textContent = 'SEARCHING…';
        open();
        const params = new URLSearchParams();
        params.set('q', query);
        params.set('resources[type]', 'article');
        params.set('resources[limit]', '10');
        try {
          const response = await fetch(
            `${rootUrl}search/suggest.json?${params.toString()}`,
            {
              signal: controller.signal,
              headers: { Accept: 'application/json' }
            }
          );
          if (!response.ok) {
            throw new Error('Predictive search failed');
          }
          const data = await response.json();
          const allArticles = data?.resources?.results?.articles || [];
          const articles = blogUrl
            ? allArticles.filter((article) =>
                String(article.url || '').startsWith(`${blogUrl}/`)
              )
            : allArticles;
          renderResults({ faqs, articles });
        } catch (error) {
          if (error.name === 'AbortError') return;
          /*
           * FAQ search is local, so it should keep working even if Shopify's
           * predictive-search request fails.
           */
          if (faqs.length) {
            renderResults({ faqs, articles: [] });
            return;
          }
          status.textContent = searchFaqs
            ? 'SEARCH UNAVAILABLE'
            : 'SEARCH UNAVAILABLE — PRESS ENTER TO SEARCH';
        }
      };
      input.addEventListener('input', () => {
        window.clearTimeout(timer);
        timer = window.setTimeout(() => run(input.value), 180);
      });
      input.addEventListener('focus', () => {
        if (input.value.trim().length >= 2) {
          run(input.value);
        }
      });
      input.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          close();
          input.blur();
          return;
        }
        if (event.key === 'Enter' && input.value.trim()) {
          const firstResult = results.querySelector(
            '.tvati-blog-search-result'
          );
          if (searchFaqs && firstResult) {
            event.preventDefault();
            firstResult.click();
            return;
          }
          event.preventDefault();
          const url = new URL(searchUrl, window.location.origin);
          url.searchParams.set('q', input.value.trim());
          url.searchParams.set('type', 'article');
          window.location.href = url.toString();
        }
      });
      clear?.addEventListener('click', () => {
        input.value = '';
        clear.hidden = true;
        clearResults();
        close();
        input.focus();
      });
      document.addEventListener('click', (event) => {
        if (!search.contains(event.target)) {
          close();
        }
      });
    });
  };
  /* ------------------------------------------------------------------------
     Article source references
     ------------------------------------------------------------------------
     Inline source numbers remain normal external links if JavaScript is not
     available. When JS is running, they open a compact source dialog first so
     the reader can see where the citation goes before leaving the article.
     ------------------------------------------------------------------------ */
  const SOURCE_REF_SELECTOR = '[data-tvati-source-ref]';
  let lastSourceTrigger = null;
  const ensureSourceDialog = () => {
    let dialog = document.querySelector('[data-tvati-source-dialog]');
    if (dialog) return dialog;
    dialog = document.createElement('dialog');
    dialog.className = 'tvati-source-dialog';
    dialog.setAttribute('data-tvati-source-dialog', '');
    dialog.setAttribute('aria-labelledby', 'tvati-source-dialog-title');
    dialog.innerHTML = `
      <div class="tvati-source-dialog__card">
        <button
          class="tvati-source-dialog__close"
          type="button"
          aria-label="Close source"
          data-tvati-source-dialog-close
        >×</button>
        <div class="tvati-source-dialog__eyebrow">SOURCE</div>
        <h2
          class="tvati-source-dialog__title"
          id="tvati-source-dialog-title"
          data-tvati-source-dialog-title
        ></h2>
        <div
          class="tvati-source-dialog__host"
          data-tvati-source-dialog-host
        ></div>
        <div
          class="tvati-source-dialog__url"
          data-tvati-source-dialog-url
        ></div>
        <div class="tvati-source-dialog__actions">
          <a
            class="tvati-source-dialog__open"
            href="#"
            target="_blank"
            rel="noopener noreferrer"
            data-tvati-source-dialog-open
          >OPEN SOURCE ↗</a>
          <button
            class="tvati-source-dialog__dismiss"
            type="button"
            data-tvati-source-dialog-close
          >CLOSE</button>
        </div>
      </div>
    `;
    document.body.appendChild(dialog);
    dialog.querySelectorAll('[data-tvati-source-dialog-close]').forEach((button) => {
      button.addEventListener('click', () => dialog.close());
    });
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) dialog.close();
    });
    dialog.addEventListener('close', () => {
      if (lastSourceTrigger?.isConnected) {
        lastSourceTrigger.focus();
      }
    });
    return dialog;
  };
  const initArticleSources = (root = document) => {
    const refs = [];
    if (root.matches?.(SOURCE_REF_SELECTOR)) {
      refs.push(root);
    }
    root.querySelectorAll?.(SOURCE_REF_SELECTOR).forEach((ref) => {
      refs.push(ref);
    });
    if (!refs.length) return;
    const dialog = ensureSourceDialog();
    const titleNode = dialog.querySelector('[data-tvati-source-dialog-title]');
    const hostNode = dialog.querySelector('[data-tvati-source-dialog-host]');
    const urlNode = dialog.querySelector('[data-tvati-source-dialog-url]');
    const openLink = dialog.querySelector('[data-tvati-source-dialog-open]');
    refs.forEach((ref) => {
      if (ref.dataset.tvatiSourceReady === 'true') return;
      ref.dataset.tvatiSourceReady = 'true';
      ref.addEventListener('click', (event) => {
        /*
         * Progressive enhancement: browsers without native <dialog> support
         * simply follow the source link in a new tab as normal.
         */
        if (typeof dialog.showModal !== 'function') return;
        const url = ref.href;
        if (!url) return;
        event.preventDefault();
        lastSourceTrigger = ref;
        const sourceTitle =
          ref.dataset.sourceTitle?.trim() ||
          ref.getAttribute('aria-label')?.replace(/^Source\s*\d*:\s*/i, '') ||
          'External source';
        let sourceHost = '';
        try {
          sourceHost = new URL(url).hostname.replace(/^www\./, '');
        } catch (error) {
          sourceHost = 'External source';
        }
        titleNode.textContent = sourceTitle;
        hostNode.textContent = sourceHost;
        urlNode.textContent = url;
        openLink.href = url;
        if (!dialog.open) {
          dialog.showModal();
        }
      });
    });
  };
  const init = (root = document) => {
    initCards(root);
    initSearch(root);
    initArticleSources(root);
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', (event) => {
    init(event.target);
  });
})();
